import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { capacityApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { AlertTriangle, TrendingUp, TrendingDown, Users, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const EAGLE_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/h25ygx96_Transparent%20BG%20Eagle.png';

const Capacity = () => {
  const [capacityData, setCapacityData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCapacity = async () => {
      try {
        const response = await capacityApi.getOverview();
        setCapacityData(response.data);
      } catch (error) {
        toast.error('Failed to fetch capacity data');
      } finally {
        setLoading(false);
      }
    };
    fetchCapacity();
  }, []);

  const getAvatarColor = (name) => {
    const colors = ['#106EFD', '#40CFDE', '#37C563', '#FF801D', '#E171EA', '#FFD166'];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
  };

  const getUtilizationColor = (utilization) => {
    if (utilization > 100) return 'text-[#FF801D]';
    if (utilization > 80) return 'text-[#FFD166]';
    if (utilization < 50) return 'text-[#106EFD]';
    return 'text-[#37C563]';
  };

  const getProgressColor = (utilization) => {
    if (utilization > 100) return 'bg-[#FF801D]';
    if (utilization > 80) return 'bg-[#FFD166]';
    if (utilization < 50) return 'bg-[#106EFD]';
    return 'bg-[#37C563]';
  };

  const overloadedCount = capacityData.filter(m => m.is_overloaded).length;
  const underutilizedCount = capacityData.filter(m => m.is_underutilized).length;
  const avgUtilization = capacityData.length > 0 
    ? Math.round(capacityData.reduce((sum, m) => sum + m.utilization_percentage, 0) / capacityData.length)
    : 0;
  const totalTasks = capacityData.reduce((sum, m) => sum + m.active_tasks_count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="capacity-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Capacity Planning</h1>
        <p className="text-navy/60 mt-1">Monitor team workload and utilization</p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-navy/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-navy/60">Team Size</p>
                <p className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">{capacityData.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#106EFD]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#106EFD]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-navy/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-navy/60">Avg Utilization</p>
                <p className={`text-3xl font-bold font-['Amaranth'] ${getUtilizationColor(avgUtilization)}`}>
                  {avgUtilization}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#40CFDE]/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#40CFDE]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-navy/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-navy/60">Overloaded</p>
                <p className="text-3xl font-bold text-[#FF801D] font-['Amaranth']">{overloadedCount}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#FF801D]/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#FF801D]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-navy/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-navy/60">Underutilized</p>
                <p className="text-3xl font-bold text-[#106EFD] font-['Amaranth']">{underutilizedCount}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#106EFD]/10 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-[#106EFD]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Recommendations */}
      {(overloadedCount > 0 || underutilizedCount > 0) && (
        <Card className="bg-gradient-to-r from-[#83E3DE]/20 to-[#E171EA]/20 border-none">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <img src={EAGLE_MASCOT} alt="Eagle" className="w-16 h-16" />
              <div>
                <h3 className="font-['Amaranth'] font-bold text-[#1A2B5E] flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#E171EA]" />
                  AI Staffing Recommendations
                </h3>
                <div className="mt-2 space-y-2 text-sm text-navy/70">
                  {overloadedCount > 0 && (
                    <p>• {overloadedCount} team member{overloadedCount > 1 ? 's are' : ' is'} overloaded. Consider reassigning some tasks.</p>
                  )}
                  {underutilizedCount > 0 && (
                    <p>• {underutilizedCount} team member{underutilizedCount > 1 ? 's have' : ' has'} available capacity for new assignments.</p>
                  )}
                  <p>• Review task priorities and consider load balancing across the team.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team capacity list */}
      <Card className="bg-white border-navy/10">
        <CardHeader>
          <CardTitle className="font-['Amaranth']">Team Workload</CardTitle>
        </CardHeader>
        <CardContent>
          {capacityData.length === 0 ? (
            <div className="empty-state py-8">
              <img src={EAGLE_MASCOT} alt="Eagle" className="w-24 h-24 mb-3" />
              <p className="text-navy/60">No team capacity data available</p>
            </div>
          ) : (
            <div className="space-y-4">
              {capacityData
                .sort((a, b) => b.utilization_percentage - a.utilization_percentage)
                .map((member) => (
                <Link key={member.user_id} to={`/team/${member.user_id}`}>
                  <div 
                    className="p-4 rounded-lg border border-navy/10 hover:border-navy/20 hover:shadow-sm transition-all"
                    data-testid={`capacity-card-${member.user_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="w-12 h-12">
                        <AvatarFallback 
                          className="font-bold text-white"
                          style={{ backgroundColor: getAvatarColor(member.name) }}
                        >
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-[#1A2B5E]">{member.name}</h4>
                          {member.is_overloaded && (
                            <Badge className="bg-[#FF801D]/10 text-[#FF801D] border-[#FF801D]/20">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Overloaded
                            </Badge>
                          )}
                          {member.is_underutilized && (
                            <Badge className="bg-[#106EFD]/10 text-[#106EFD] border-[#106EFD]/20">
                              Available
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-navy/60 capitalize">{member.role?.replace('_', ' ')}</p>
                        
                        {/* Progress bar */}
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-navy/60">
                              {member.current_allocated_hours}h / {member.weekly_capacity_hours}h
                            </span>
                            <span className={`font-medium ${getUtilizationColor(member.utilization_percentage)}`}>
                              {member.utilization_percentage}%
                            </span>
                          </div>
                          <div className="h-2 rounded-full bg-navy/10 overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${getProgressColor(member.utilization_percentage)}`}
                              style={{ width: `${Math.min(member.utilization_percentage, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right hidden sm:block">
                        <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">
                          {member.active_tasks_count}
                        </p>
                        <p className="text-xs text-navy/50">active tasks</p>
                      </div>
                    </div>
                    
                    {/* Expertise tags */}
                    {member.expertise_areas?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-navy/5">
                        <div className="flex flex-wrap gap-1">
                          {member.expertise_areas.slice(0, 4).map((area, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">{area}</Badge>
                          ))}
                          {member.expertise_areas.length > 4 && (
                            <Badge variant="outline" className="text-xs">
                              +{member.expertise_areas.length - 4}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Capacity;
